import { useEffect } from 'react';
import trackerConfig from './tracker.json';

const DEFAULT_ENDPOINT = trackerConfig?.defaults?.endpoint || 'https://analytics.insightos.co.uk/data/bucket';
const DEFAULT_BUCKET = trackerConfig?.defaults?.bucket || 'task-management';

const slugify = (value) =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const buildMatchers = (rule = {}) => {
  const aliases = rule.aliases && rule.aliases.length ? rule.aliases : [rule.canonical];
  return aliases
    .filter(Boolean)
    .map((alias) => {
      const wildcard = alias.endsWith('*');
      const value = wildcard ? alias.slice(0, -1) : alias;
      return { wildcard, value: slugify(value) };
    });
};

const componentRuleIndex = (trackerConfig?.componentRules || []).map((rule) => ({
  rule,
  matchers: buildMatchers(rule),
}));

const featureRuleIndex = (trackerConfig?.featureRules || []).map((rule) => ({
  rule,
  matchers: buildMatchers(rule),
}));

const bucketIndex = Object.entries(trackerConfig?.buckets || {}).map(([id, bucket]) => ({
  id,
  label: bucket?.label || id,
  aliases: new Set([
    slugify(id),
    ...(bucket?.aliases || []).map((alias) => slugify(alias)),
  ]),
}));

const normalizationConfig = trackerConfig?.normalization || {};
const featureSuffixes = normalizationConfig.featureSuffixes || [];

const matchRule = (value, ruleIndex) => {
  if (!value) return null;
  return ruleIndex.find(({ matchers }) =>
    matchers.some(({ wildcard, value: matcherValue }) =>
      wildcard ? value.startsWith(matcherValue) : value === matcherValue,
    ),
  );
};

const normalizeFeature = (value) => {
  let slug = slugify(value);
  featureSuffixes.forEach((suffix) => {
    if (suffix && slug.endsWith(suffix)) {
      slug = slug.slice(0, -suffix.length);
    }
  });
  return slug;
};

const normalizeComponent = (value) => slugify(value);

const normalizeBucket = (rawBucket, componentBucket) => {
  const requested = slugify(rawBucket);
  if (requested) {
    const foundBucket = bucketIndex.find((bucket) => bucket.aliases.has(requested));
    if (foundBucket) {
      return foundBucket.id;
    }
  }

  if (componentBucket) {
    const componentSlug = slugify(componentBucket);
    const fallback = bucketIndex.find((bucket) => bucket.aliases.has(componentSlug));
    if (fallback) {
      return fallback.id;
    }
    return componentSlug || DEFAULT_BUCKET;
  }

  return DEFAULT_BUCKET;
};

/**
 * Global click tracker that posts component/feature clicks to the analytics service.
 * Elements opt-in by adding both `data-component` and `data-feature` attributes.
 */
export default function useAnalyticsTracker({
  companyCode,
  endpoint = DEFAULT_ENDPOINT,
  enabled = true,
  includeCredentials = false,
  analyticsToken,
} = {}) {
  useEffect(() => {
    if (!enabled || !companyCode || !analyticsToken) {
      return undefined;
    }

    let abortController;

    const handleClick = (event) => {
      const target = event.target.closest('[data-component][data-feature]');
      if (!target) return;

      const rawComponent = target.getAttribute('data-component');
      const rawFeature = target.getAttribute('data-feature');
      const rawBucket = target.getAttribute('data-bucket');
      const rawTimeSaved = target.getAttribute('data-time-saved');

      const normalizedComponent = normalizeComponent(rawComponent);
      const normalizedFeature = normalizeFeature(rawFeature);

      if (!normalizedComponent || !normalizedFeature) return;

      const componentRule = matchRule(normalizedComponent, componentRuleIndex);
      const featureRule = matchRule(normalizedFeature, featureRuleIndex);

      const component = componentRule?.rule?.canonical || normalizedComponent;
      const feature = featureRule?.rule?.canonical || normalizedFeature;
      const bucket = normalizeBucket(rawBucket, componentRule?.rule?.bucket);
      const parsedTimeSaved = Number.parseFloat(rawTimeSaved);
      const timeSaved = Number.isFinite(parsedTimeSaved) ? parsedTimeSaved : 0;

      if (!component || !feature) return;

      abortController?.abort();
      abortController = new AbortController();

      const payload = {
        companycode: companyCode || 'unknown',
        component,
        feature,
        bucket,
        timeSaved,
        metadata: {
          rawComponent,
          rawFeature,
          rawBucket,
          rawTimeSaved,
        },
      };

      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${analyticsToken}`,
        },
        body: JSON.stringify(payload),
        credentials: includeCredentials ? 'include' : 'omit',
        signal: abortController.signal,
        keepalive: true,
      }).catch((error) => {
        if (error.name !== 'AbortError') {
          console.warn('Analytics track failed', error);
        }
      });
    };

    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      abortController?.abort();
    };
  }, [analyticsToken, companyCode, enabled, endpoint, includeCredentials]);
}
